import { h, Component, State, Element, Prop, Watch, Host, EventEmitter, Event, Listen } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { InfoIconSchemeDefinition, InfoIconSchemeName, InfoIconSize } from "./constants/info-icon-types";
import { INFO_ICON_SCHEME_CONFIG, INFO_ICON_SCHEME_MAP } from "./constants/info-icon-constants";
import { openModal } from "../../utils/openModal";
import { InfoIconSchemeConfiguration } from "../../constants/colors";
import { INFO_ICON_ICON_NAME } from "../../constants/global-constants";
import { ICONSIZE_NORMAL } from "../icon/constants/icon-constants";
import pfx from "../../utils/style-class-prefix-helper";
import {
  TOOLTIP_VISIBILITY_ALWAYS,
  TOOLTIP_VISIBILITY_ON_CLICK,
  TOOLTIP_VISIBILITY_ON_HOVER
} from "../tooltip/constants/tooltip-constants";
import { KeyIds } from "../../constants/keys";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 * @slot tooltip - holds eon-ui-tooltip component
 */
@Component({
  tag: "eon-ui-info-icon",
  styleUrl: "info-icon.scss",
  shadow: true
})
export class InfoIcon implements ColorableComponent<InfoIconSchemeConfiguration<InfoIconSchemeDefinition>> {
  schemeConfig: InfoIconSchemeConfiguration<InfoIconSchemeDefinition> = INFO_ICON_SCHEME_CONFIG;
  schemeMap = INFO_ICON_SCHEME_MAP;

  tooltipElement: HTMLEonUiTooltipElement;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: InfoIconSchemeDefinition;
  @State() isVisibilityOnClick: string;

  @Element() hostElement: HTMLEonUiInfoIconElement;

  /**
   * Raised when the button has been clicked.
   */
  @Event() infoIconClick: EventEmitter;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: InfoIconSchemeName;

  /**
   * Whether this button is disabled or not. Disabled buttons can't be clicked and emit no events.
   */
  @Prop({ reflect: true }) disabled: boolean;

  /**
   * Specifies the label to use for accessibility.
   * Always provide this for pure icon buttons!
   */
  @Prop({ mutable: true }) ariaLabel: string;

  /**
   * use this property to open a modal window, the modal must have the same modal-id
   * see component: Modal
   */
  @Prop() openModalId: string;

  /**
   * Info-icon size
   */
  @Prop({ mutable: true, reflect: true }) size: InfoIconSize = ICONSIZE_NORMAL;

  componentWillLoad() {
    this.applyScheme();
    this.setAriaLabel();
    this.getTooltipElement();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  private setAriaLabel() {
    this.ariaLabel = this.ariaLabel || "info";
  }

  handleClick(_event: Event) {
    if (!!this.openModalId) {
      openModal(this.openModalId);
    }

    this.infoIconClick.emit();
  }

  /**
   * Tooltip Check for OnClick visibility property
   */
  private getTooltipElement() {
    this.tooltipElement = this.hostElement.querySelector('[slot="tooltip"]');
    this.isVisibilityOnClick = ((this.tooltipElement as unknown) as HTMLEonUiTooltipElement)?.visibility;
  }

  handleFocusEvent() {
    const tooltipElements = Array.from(document.querySelectorAll("eon-ui-tooltip"));
    tooltipElements.map((item) => {
      item.visibility !== TOOLTIP_VISIBILITY_ALWAYS && (item.visible = false);
      item.visibility === TOOLTIP_VISIBILITY_ON_HOVER &&
        item.parentElement === this.hostElement &&
        (item.visible = true);
    });
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.key == KeyIds.ENTER || e.key == KeyIds.SPACE) {
      this.tooltipElement && (this.tooltipElement.visible = true);
    }
  }

  @Listen("click", { target: "window" })
  onDocumentClick(event) {
    if (
      event.target !== this.hostElement &&
      event.target !== this.tooltipElement &&
      event.target.parentElement !== this.tooltipElement
    ) {
      this.tooltipElement &&
        this.tooltipElement.visibility === TOOLTIP_VISIBILITY_ON_CLICK &&
        (this.tooltipElement.visible = false);
    }
  }

  private getTooltipProps() {
    let props = {
      onKeyDown: (event: KeyboardEvent) => this.handleKeyDown(event),
      onFocus: () => this.handleFocusEvent()
    };

    return props;
  }

  render() {
    return (
      <Host>
        <button
          class={pfx({
            [`visibility-${this.isVisibilityOnClick}`]: !!this.isVisibilityOnClick,
            "tooltip-icon": this.tooltipElement
          })}
          aria-label={this.ariaLabel}
          tabIndex={0}
          disabled={this.disabled}
          type="button"
          onClick={(event: MouseEvent) => this.handleClick(event)}
          {...(this.tooltipElement && this.getTooltipProps())}
        >
          <eon-ui-icon
            aria-label={this.ariaLabel}
            name={INFO_ICON_ICON_NAME}
            size={this.size}
            alignment-x="center"
            alignment-y="center"
            no-space="true"
            default-height="true"
          ></eon-ui-icon>
        </button>
        <slot name="tooltip" />
      </Host>
    );
  }
}
